/**
 * Alerta de mensagem nova do atendimento: detecção (pura) + som (Web Audio).
 *
 * A detecção compara duas leituras da lista de conversas. A PRIMEIRA leitura só "semeia" o
 * estado (senão abrir a tela tocaria para tudo que já estava lá). O som é sintetizado no
 * navegador (dois tons curtos), sem arquivo de áudio e sem dependência.
 */
import type { PlatformConversation } from "../../api/types.js";
import { lastSenderOf } from "./attention.js";

export interface InboxSnapshotEntry { at: string; unread: number }
export type InboxSnapshot = Map<string, InboxSnapshotEntry>;

export interface NewInboundAlert {
  conversationId: string;
  name: string;
  preview: string;
  pictureUrl: string | null;
  at: string;
}

const activityAt = (conversation: PlatformConversation): string => conversation.messages.at(-1)?.createdAt ?? conversation.lastMessageAt ?? conversation.updatedAt ?? "";

export function snapshotOf(conversations: PlatformConversation[]): InboxSnapshot {
  return new Map(conversations.map((conversation) => [conversation.id, { at: activityAt(conversation), unread: conversation.unreadCount ?? 0 }]));
}

/**
 * Mensagens novas DO CLIENTE desde `previous`. `null` = primeira leitura (nada a avisar).
 * Conta como nova a conversa cuja atividade avançou (ou cujas não lidas subiram) com o cliente
 * por último; conversa inédita entra como nova. `isOpen` silencia a conversa que o operador
 * está olhando agora (ele já vê a mensagem chegar).
 */
export function detectNewInbound(
  previous: InboxSnapshot | null,
  conversations: PlatformConversation[],
  options: { isOpen?: (conversationId: string) => boolean; describe: (conversation: PlatformConversation) => { name: string; pictureUrl: string | null } }
): NewInboundAlert[] {
  if (previous === null) return [];
  const alerts: NewInboundAlert[] = [];
  for (const conversation of conversations) {
    // Só o cliente dispara alerta. Sem `lastSender` conhecido, não lidas fazem o papel dele.
    const sender = lastSenderOf(conversation);
    if (sender !== null ? sender !== "customer" : (conversation.unreadCount ?? 0) === 0) continue;
    const before = previous.get(conversation.id);
    const at = activityAt(conversation);
    const advanced = before === undefined ? (conversation.unreadCount ?? 0) > 0 : at > before.at || (conversation.unreadCount ?? 0) > before.unread;
    if (!advanced) continue;
    if (options.isOpen?.(conversation.id)) continue;
    const { name, pictureUrl } = options.describe(conversation);
    alerts.push({ conversationId: conversation.id, name, preview: conversation.lastMessagePreview ?? conversation.messages.at(-1)?.text ?? "Nova mensagem", pictureUrl, at });
  }
  return alerts;
}

const MUTE_KEY = "super2.inbox.alertSound";

/** Som ligado por padrão; a escolha do operador fica no navegador (e nunca quebra se o storage falhar). */
export function readSoundEnabled(): boolean {
  try { return globalThis.localStorage?.getItem(MUTE_KEY) !== "off"; } catch { return true; }
}
export function writeSoundEnabled(enabled: boolean): void {
  try { globalThis.localStorage?.setItem(MUTE_KEY, enabled ? "on" : "off"); } catch { /* sem storage: vale só nesta sessão */ }
}

let audioContext: AudioContext | null = null;

/** Dois tons curtos (sol → dó agudo), no estilo "mensagem recebida". Silencioso se o navegador bloquear o áudio. */
export function playMessageSound(): void {
  try {
    const Context = (globalThis as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext ?? (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Context) return;
    audioContext ??= new Context();
    const context = audioContext;
    if (context.state === "suspended") void context.resume();
    const start = context.currentTime;
    [[784, 0], [1047, 0.12]].forEach(([frequency, offset]) => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = "sine";
      oscillator.frequency.value = frequency!;
      gain.gain.setValueAtTime(0.0001, start + offset!);
      gain.gain.exponentialRampToValueAtTime(0.18, start + offset! + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + offset! + 0.22);
      oscillator.connect(gain).connect(context.destination);
      oscillator.start(start + offset!);
      oscillator.stop(start + offset! + 0.25);
    });
  } catch { /* áudio indisponível ou bloqueado até o primeiro clique: o aviso visual continua valendo */ }
}
